import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';

const MAX_CART_ITEMS = 20;

export interface CartItem {
  nft_token_id: string;
  name: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  collection_name: string | null;
  collection_slug: string | null;
  owner_address: string;
  offer_id: string;
  price_drops: number;
  price_xrp: number;
  destination: string | null;
  expiration: number | null;
  added_at: number;
  rarity_rank: number | null;
}

interface CartStore {
  items: CartItem[];
  isDrawerOpen: boolean;

  addItem: (item: CartItem) => boolean;
  removeItem: (nft_token_id: string) => void;
  clearCart: () => void;
  isInCart: (nft_token_id: string) => boolean;
  removeExpiredItems: () => CartItem[];
  removeItems: (ids: string[]) => void;
  setDrawerOpen: (open: boolean) => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isDrawerOpen: false,

      addItem: (item: CartItem): boolean => {
        const state = get();

        // Check duplicate
        if (state.items.some((i) => i.nft_token_id === item.nft_token_id)) {
          return false;
        }

        // Check cart full
        if (state.items.length >= MAX_CART_ITEMS) {
          return false;
        }

        // Check own NFT
        const user = useAuthStore.getState().user;
        if (user && item.owner_address === user.xrpl_address) {
          return false;
        }

        set({ items: [...state.items, item] });
        return true;
      },

      removeItem: (nft_token_id: string) => {
        set((state) => ({
          items: state.items.filter((i) => i.nft_token_id !== nft_token_id),
        }));
      },

      clearCart: () => set({ items: [] }),

      isInCart: (nft_token_id: string): boolean => {
        return get().items.some((i) => i.nft_token_id === nft_token_id);
      },

      removeExpiredItems: (): CartItem[] => {
        const now = Math.floor(Date.now() / 1000);
        const state = get();
        const expired = state.items.filter(
          (i) => i.expiration !== null && i.expiration <= now
        );
        if (expired.length > 0) {
          set({
            items: state.items.filter(
              (i) => i.expiration === null || i.expiration > now
            ),
          });
        }
        return expired;
      },

      removeItems: (ids: string[]) => {
        const idSet = new Set(ids);
        set((state) => ({
          items: state.items.filter((i) => !idSet.has(i.nft_token_id)),
        }));
      },

      setDrawerOpen: (open: boolean) => set({ isDrawerOpen: open }),
    }),
    {
      name: 'xrpl-cart-storage',
      partialize: (state) => ({
        items: state.items,
      }),
    }
  )
);
